import React from "react";

import { Badge } from "components/Graphs/Badge";
import { cx } from "components/Graphs/utils";
import { GridBackground } from "./grid-background";
import { Environment } from "components/Environment/utils/types";
import { useQuery } from "@tanstack/react-query";
import { Spinner } from "components/UI-lib";
import agent from "server";
import { useLaunchable } from "components/Launchables/utils";

interface TopBadge {
  text: string;
  variant?: "default" | "secondary" | "warning" | "success" | "info";
  className?: string;
}

interface MarketplaceLaunchableCardWrapperProps {
  environment: Environment;
  topBadge?: TopBadge;
  onClick?: (e: React.MouseEvent) => void;
  disableScale?: boolean;
  deployUrlOverride?: string;
}

interface MarketplaceLaunchableCardProps {
  environmentId: string;
  environmentName: string;
  description: string;
  username: string | null;
  loadingUser: boolean;
  topBadge?: TopBadge;
  deployUrl: string;
  onDeploy: (e: React.MouseEvent) => void;
  disableScale?: boolean;
}

// Presentational Component - just displays data
export function MarketplaceLaunchableCard({
  environmentId,
  environmentName,
  description,
  username,
  loadingUser,
  topBadge,
  deployUrl,
  onDeploy,
  disableScale = false,
}: MarketplaceLaunchableCardProps) {
  return (
    <GridBackground
      className="h-full"
      deployUrl={deployUrl}
      onClick={onDeploy}
      disableScale={disableScale}
    >
      <div
        className={cx(
          "border border-zinc-800 bg-primaryBg p-4 cursor-pointer transition-all duration-300 hover:shadow-lg h-full flex flex-col justify-between rounded-md",
        )}
      >
        <div className="h-full">
          <div className="flex items-center justify-between">
            {/* Top Badge */}
            {topBadge && (
              <div className="flex items-center justify-between mb-2">
                <Badge
                  variant={topBadge.variant || "secondary"}
                  className={cx("text-xs", topBadge.className)}
                >
                  {topBadge.text}
                </Badge>
              </div>
            )}
          </div>

          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-lg text-primary transition-colors">
                {environmentName}
              </h1>
              <p className="text-sm text-secondary">by {username}</p>
              <div className="mt-4">
                <p className="text-sm text-secondary line-clamp-2">
                  {description}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </GridBackground>
  );
}

// Wrapper Component - contains all the logic
export function MarketplaceLaunchableCardWrapper({
  environment,
  topBadge,
  onClick,
  disableScale = false,
  deployUrlOverride,
}: MarketplaceLaunchableCardWrapperProps) {
  const environmentId = environment.environment_id || "";
  const environmentName = environment.name || "";

  const deployUrl =
    deployUrlOverride ??
    `${
      window.location.origin
    }/launchable/deploy?launchableID=${encodeURIComponent(environmentId)}`;

  const handleDeploy = (e: React.MouseEvent) => {
    // If onClick is provided, use it (for sidebar selection behavior)
    // Otherwise let the anchor tag handle navigation (default behavior)
    if (onClick) {
      e.preventDefault();
      e.stopPropagation();
      onClick(e);
    }
  };

  // Check if environment has build object
  const hasBuildObject = !!environment.build;

  if (hasBuildObject) {
    // Path 1: Environment has build object from search results
    return (
      <EnvironmentCard
        environment={environment}
        topBadge={topBadge}
        deployUrl={deployUrl}
        onDeploy={handleDeploy}
        disableScale={disableScale}
      />
    );
  } else {
    // Path 2: only environment ID is available, hand picked data here on homescreen
    return (
      <EnvironmentOnlyIDCard
        environmentId={environmentId}
        environmentName={environmentName}
        topBadge={topBadge}
        deployUrl={deployUrl}
        onDeploy={handleDeploy}
        disableScale={disableScale}
      />
    );
  }
}

// Component for environments WITH build object
function EnvironmentCard({
  environment,
  topBadge,
  deployUrl,
  onDeploy,
  disableScale = false,
}: {
  environment: Environment;
  topBadge?: TopBadge;
  deployUrl: string;
  onDeploy: (e: React.MouseEvent) => void;
  disableScale?: boolean;
}) {
  const environmentId = environment.environment_id || "";
  const environmentName = environment.name || "";
  const userId = environment.labels?.userId;
  const description =
    environment.meta?.description || "No description available";

  // Fetch user data
  const { data: user = null, isLoading: loadingUser } = useQuery({
    queryKey: ["public-user", userId],
    queryFn: async () => {
      const user = await agent.Users.getPublicUser(userId || "");
      return user;
    },
    enabled: !!userId,
  });

  return (
    <MarketplaceLaunchableCard
      environmentId={environmentId}
      environmentName={environmentName}
      description={description}
      username={user?.data?.username || null}
      loadingUser={loadingUser}
      topBadge={topBadge}
      deployUrl={deployUrl}
      onDeploy={onDeploy}
      disableScale={disableScale}
    />
  );
}

// Component for environments WITHOUT build object
function EnvironmentOnlyIDCard({
  environmentId,
  environmentName,
  topBadge,
  deployUrl,
  onDeploy,
  disableScale = false,
}: {
  environmentId: string;
  environmentName: string;
  topBadge?: TopBadge;
  deployUrl: string;
  onDeploy: (e: React.MouseEvent) => void;
  disableScale?: boolean;
}) {
  // Use the useLaunchable hook to get launchable data

  const {
    launchableData,
    error,
    loading: loadingLaunchable,
    launchableNotFound,
    launchableAccessError,
  } = useLaunchable(environmentId, true);

  const userId = launchableData?.userId;
  const description = launchableData?.description || "No description available"; // Launchable data doesn't have description in the same way

  // Fetch user data
  const { data: user = null, isLoading: loadingUser } = useQuery({
    queryKey: ["public-user", userId],
    queryFn: async () => {
      const user = await agent.Users.getPublicUser(userId || "");
      return user;
    },
    enabled: !!userId,
  });

  if (loadingLaunchable) {
    return (
      <GridBackground
        className="h-full"
        deployUrl={deployUrl}
        onClick={onDeploy}
      >
        <div
          className={cx(
            "border border-zinc-800 bg-primaryBg p-4 cursor-pointer transition-all duration-300 hover:shadow-lg h-full flex flex-col justify-center items-center rounded-md",
            topBadge ? "min-h-[170px]" : "min-h-[142px]",
          )}
        >
          <Spinner type="primary" />
        </div>
      </GridBackground>
    );
  }

  if (launchableNotFound || launchableAccessError || error) {
    return (
      <GridBackground
        className="h-full"
        deployUrl={deployUrl}
        onClick={onDeploy}
        disableScale={disableScale}
      >
        <div
          className={cx(
            "border border-zinc-800 bg-primaryBg p-4 cursor-pointer transition-all duration-300 hover:shadow-lg h-full flex flex-col justify-center items-center rounded-md",
          )}
        >
          <p className="text-sm text-secondary">
            {launchableNotFound
              ? "Launchable not found"
              : launchableAccessError
                ? "Access denied"
                : "Error loading launchable"}
          </p>
        </div>
      </GridBackground>
    );
  }

  return (
    <MarketplaceLaunchableCard
      environmentId={environmentId}
      environmentName={launchableData?.name || environmentName}
      description={description}
      username={user?.data?.username || null}
      loadingUser={loadingUser}
      topBadge={topBadge}
      deployUrl={deployUrl}
      onDeploy={onDeploy}
      disableScale={disableScale}
    />
  );
}
